import { ArcoDto, TrasferimentoModalita, useGetTrasferimenti } from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { usePosizioneFilterWithNavigation } from "@/hooks/usePosizioneFilterWithNavigation";
import { portaleConfig } from "@/portaliConfig";
import { getCalendarMessages } from "@/utils/calendarioUtils";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { Autocomplete, Checkbox, TextField, Tooltip } from "@mui/material";
import moment, { Moment } from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Calendar, momentLocalizer, View, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading";
import "./calendar.css";

const localizer = momentLocalizer(moment);

const views = [Views.MONTH, Views.AGENDA];

type Evento = {
  date: Moment;
  trasferimento: ArcoDto;
  tipo: "inizio" | "fine";
};

const colorsByModalita: {
  [type in TrasferimentoModalita | "unknown"]: {
    getLabel: (t: TFunction) => string;
    backgroundColor: string;
    color?: string;
    blinking?: boolean;
  };
} = {
  noleggio: {
    getLabel: (t) => t("calendarioTrasferimenti.modalita.noleggio"),
    backgroundColor: "#f8b31e",
    color: "#E0E0E0",
  },
  venduto: {
    getLabel: (t) => t("calendarioTrasferimenti.modalita.venduto"),
    backgroundColor: "#877a9b",
    color: "#E0E0E0",
  },
  service: {
    getLabel: (t) => t("calendarioTrasferimenti.modalita.service"),
    backgroundColor: "#885c56",
    color: "#E0E0E0",
  },
  unknown: {
    getLabel: (t) => t("calendarioTrasferimenti.modalita.sconosciuta"),
    backgroundColor: "#eeeeee",
    color: "#222222",
  },
};

function CalendarioTrasferimentiPage() {
  const { t } = useTranslation();
  const calendarMessages = useMemo(() => getCalendarMessages(t), [t]);
  const { filters, filtersLoading, changeFilter } =
    usePosizioneFilterWithNavigation("/calendario-trasferimenti", {
      enableDate: true,
    });

  const date = useMemo(() => {
    return filters.date ?? moment().startOf("month").toDate();
  }, [filters.date]);

  const onNavigate = useCallback((newDate: Date) => {
    changeFilter("date")(null, moment(newDate).startOf("month").toDate());
  }, []);

  const { hasPermission } = useAuth();

  const [selectedModalita, setSelectedModalita] = useState<
    (TrasferimentoModalita | "unknown")[]
  >([]);

  const { data: trasferimenti, isLoading: trasferimentiLoading } =
    useGetTrasferimenti(
      portaleConfig.id,
      {
        da: moment(date).startOf("month").toISOString(),
        a: moment(date).endOf("month").toISOString(),
      },
      {
        query: {
          enabled: hasPermission("CalendarioTrasferimentiRead"),
        },
      },
    );

  const loading = trasferimentiLoading;

  const [view, setView] = useState<View>(views[0]);

  const events = useMemo(() => {
    const events: Evento[] = [];

    if (trasferimenti?.data) {
      for (const t of trasferimenti.data) {
        const modalita = t.trasferimentoModalita ?? "unknown";

        if (
          selectedModalita.length > 0 &&
          !selectedModalita.includes(modalita)
        ) {
          continue;
        }

        // Add event for "da" date if it exists
        if (t.da) {
          events.push({
            date: moment(t.da),
            trasferimento: t,
            tipo: "inizio",
          });
        }

        // Add event for "a" date if it exists and is different from "da"
        if (t.a && t.a !== t.da) {
          events.push({
            date: moment(t.a),
            trasferimento: t,
            tipo: "fine",
          });
        }
      }
    }

    return events;
  }, [trasferimenti, selectedModalita]);

  const renderTitle = useMemo(() => {
    const TitoloEvento = ({ evento }: { evento: Evento }) => {
      const trasferimento = evento.trasferimento;
      const { color, getLabel } =
        colorsByModalita[trasferimento.trasferimentoModalita ?? "unknown"];
      const label = getLabel(t);

      const path = useMemo(() => {
        return (
          trasferimento.sorgente?.breadcrumbs
            ?.map((x) => x.nodo.nome)
            ?.join(" > ") ?? ""
        );
      }, [trasferimento]);

      const tooltip = useMemo(() => {
        let tooltip = t("calendarioTrasferimenti.tooltip.titolo");
        if (trasferimento.da) {
          tooltip += t("calendarioTrasferimenti.tooltip.da", {
            data: moment(trasferimento.da).format("DD/MM/YYYY"),
          });
        }
        if (trasferimento.a) {
          tooltip += t("calendarioTrasferimenti.tooltip.a", {
            data: moment(trasferimento.a).format("DD/MM/YYYY"),
          });
        }

        if (trasferimento.trasferimentoModalita) {
          tooltip += t("calendarioTrasferimenti.tooltip.modalita", {
            modalita: label,
          });
        }

        return (
          <Typography fontSize={12} sx={{ textAlign: "start" }}>
            {tooltip}
            <br />
            <br />
            {path}
          </Typography>
        );
      }, [trasferimento, path, color, label, t]);

      return (
        <Tooltip
          title={tooltip}
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: "420px",
                backgroundColor: "#dddddd",
              },
            },
          }}
        >
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row" alignItems="center">
              {evento.tipo === "inizio" ? (
                // l'aereo parte da Gatti verso il cliente
                <FlightTakeoffIcon sx={{ color: color }} />
              ) : (
                // l'aereo atterra da Gatti
                <FlightLandIcon sx={{ color: color }} />
              )}
              <Typography sx={{ color, ml: 0.5 }}>
                {trasferimento.destinazione?.dispositivoMatricola ?? ""}
              </Typography>
            </Box>

            <Box display="flex" flexDirection="row" gap={0.5}>
              <Typography sx={{ color, fontSize: "0.75rem" }}>
                {path}
              </Typography>
            </Box>
          </Box>
        </Tooltip>
      );
    };

    return (e: any) => (<TitoloEvento evento={e as Evento} />) as any;
  }, [view, t]);

  const navigate = useNavigate();

  function handleEventSelect(evento: Evento) {
    // Navigate to the source node of the transfer if available
    if (evento.trasferimento.destinazioneId) {
      navigate(`/nodi/${evento.trasferimento.destinazioneId}`);
    }
  }

  return (
    <Box
      p={2}
      sx={{
        height: "calc(100vh - 96px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography variant="h4" mr={2}>
          {t("calendarioTrasferimenti.titolo")}
        </Typography>

        <Box display="flex" gap={1} pt={2} flexWrap="wrap">
          <TrasferimentoModalitaPicker
            selected={selectedModalita}
            onChange={setSelectedModalita}
          />
        </Box>
      </Box>

      <Box
        mt={4}
        mb={0}
        display="flex"
        flexDirection="row"
        flexWrap="wrap"
        gap={2}
        flex={1}
        sx={{
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            opacity: loading ? 0.25 : 1,
            "& .rbc-agenda-table .rbc-header:nth-child(2)": { display: "none" },
            "& .rbc-agenda-table .rbc-agenda-time-cell": { display: "none" },
          }}
        >
          <Calendar
            messages={calendarMessages}
            views={views}
            localizer={localizer}
            events={events}
            startAccessor="date"
            endAccessor="date"
            titleAccessor={renderTitle}
            eventPropGetter={eventPropGetter}
            popup
            view={view as View}
            onView={(v) => setView(v)}
            onSelectEvent={handleEventSelect}
            date={date}
            onNavigate={onNavigate}
          />
        </Box>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Loading />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TrasferimentoModalitaPicker({
  selected,
  onChange,
}: {
  selected: (TrasferimentoModalita | "unknown")[];
  onChange: (selected: (TrasferimentoModalita | "unknown")[]) => void;
}) {
  const { t } = useTranslation();
  const modalitaOptions = useMemo(
    () =>
      Object.entries(colorsByModalita).map(([key, config]) => ({
        key: key as TrasferimentoModalita | "unknown",
        label: config.getLabel(t),
        backgroundColor: config.backgroundColor,
      })),
    [t],
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={modalitaOptions}
      getOptionLabel={(option) => option.label}
      value={modalitaOptions.filter((option) => selected.includes(option.key))}
      onChange={(_, value) => onChange(value.map((v) => v.key))}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: option.backgroundColor,
              marginRight: 1,
            }}
          />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selected.length === 0
              ? t("calendarioTrasferimenti.filtri.modalitaPlaceholder")
              : undefined
          }
          sx={{ minWidth: 240 }}
        />
      )}
    />
  );
}

const eventPropGetter = (evento: Evento) => {
  const config =
    colorsByModalita[evento.trasferimento.trasferimentoModalita ?? "unknown"];
  return {
    style: {
      backgroundColor: config.backgroundColor,
      color: config.color,
    },
  };
};

export default CalendarioTrasferimentiPage;
